import { useEffect, useRef, useState } from 'react';
import { TypeMenuSearchProps } from './types';
import { Container } from '@/atoms';
import Plant from '@/stories/assets/pre-footer/plant.png';
import { ChevronRight, Close, Search } from '@/icons';
import { useDebouncedCallback } from "use-debounce";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { MoonLoader } from 'react-spinners';
import { SearchItems } from './SearchItems';
import Link from 'next/link';
import { useScrollBlock } from '@/hooks/useScrollBlock';
import { DrupalNode, deserialize } from 'next-drupal';
import { getParams } from '@/utils/drupal/get-params';
import { useRouter } from 'next/router';
import { searchLimit } from '@/lib/constants';

/**
 * Menu
 */
export const MenuSearch = ({
  menuSearchOpen,
  setMenuSearchOpen,
  searchSuggestions
}: TypeMenuSearchProps) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialize, setInitialize] = useState(false);
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [zeroResults, setZeroResults] = useState(false);
  const router = useRouter();

  const inputRef = useRef(null);
  const popOverRef = useRef(null);
  const valueRef = useRef("");

  const [blockScroll, allowScroll] = useScrollBlock();

  function clearResults() {
    setArticles([]);
    setProducts([]);
    setRecipes([]);
    valueRef.current = "";
    setInitialize(false);
  }

  useEffect(() => {
    if (menuSearchOpen) {
      blockScroll();
      inputRef.current.focus();
    } else {
      clearResults();
      allowScroll();
      popOverRef.current.blur();
    }

    return () => {
      allowScroll();
    }
  }, [menuSearchOpen])

  useEffect(() => {
    if (inputRef.current && !isSubmitting) {
      inputRef.current.focus();
    }
  }, [isSubmitting, initialize])

  const fetchData = async (url, setData, term, type, queryFilter?) => {
    const params = getParams(type, "search");
    params.addFilter("fulltext", term);
    if (queryFilter?.sort && queryFilter?.order) {
      params.addSort(queryFilter.sort, queryFilter.order);
    } else {
      params.addSort("search_api_relevance", "DESC");
    }
    if (queryFilter?.pageLimit) {
      params.addPageLimit(queryFilter.pageLimit);
    }

    try {
      // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          deserialize: false,
          params: params.getQueryObject(),
        }),
      });
      const json = await response.json();
      const results = deserialize(json) as DrupalNode[] || [];
      setData(results);
      return results.length
    } catch (error) {
      setData([]);
    };
  }
  async function handleSubmit(event): Promise<void> {
    event.preventDefault();
    setIsSubmitting(true);
    const term = inputRef.current.value;

    const promises = [
      fetchData("/api/search/product_search", setProducts, term, "shopify_product--shopify_product", { pageLimit: searchLimit }),
      fetchData("/api/search/recipe_search", setRecipes, term, "node--recipe", { pageLimit: searchLimit }),
      fetchData("/api/search/article_search", setArticles, term, "node--article", { pageLimit: searchLimit }),
    ];

    let existsResult = false;
    await Promise.all(promises).then((valueArray) => existsResult = valueArray.some((value) => value !== 0));
    if (!existsResult) {
      setProducts(searchSuggestions.field_products);
      setRecipes(searchSuggestions.field_recipes);
      setArticles(searchSuggestions.field_articles);
      setZeroResults(true);
    } else {
      setZeroResults(false);
    }
    setIsSubmitting(false);
    if (!initialize) {
      setInitialize(true);
    }
  }

  const debounced = useDebouncedCallback(
    (event) => {
      handleSubmit(event);
    }, 1000);

  useEffect(() => {
    function changePage() {
      if (popOverRef.current.getAttribute("data-headlessui-state") === "open") {
        popOverRef.current.click();
        setMenuSearchOpen(false);
      }
    }
    router.events.on('routeChangeStart', changePage);
    return () => {
      router.events.off('routeChangeStart', changePage);
    };
  }, []);


  return (
    <Popover>
      <PopoverButton ref={popOverRef} className='lg:border-b lg:border-transparent lg:hover:border-primary-500 only:border-none'>
        <Search width={32} height={32} className='lg:w-6 lg:h-6' />
      </PopoverButton>
      <PopoverPanel static className="w-full absolute left-0 top-full overflow-y-auto max-h-[calc(100vh_-_var(--header-height))]" >
        {({ open }) => (
          <>
            {
              open ?
                <>
                  {setMenuSearchOpen(true)}
                  <Container
                    backgroundColor={initialize ? "bg-white" : "bg-primary-600"}
                    classNameWrapper="w-full bg-primary-600"
                    backgroundImage={initialize ? "" : Plant.src}
                    className='min-h-[450px] items center'
                  >
                    <div className={`max-w-5xl w-full flex flex-col self-center text-body ${initialize ? "text-text-tertiary" : "text-white"} flex-1`}>
                      <div
                        className={`flex self-center w-full h-full border-b ${initialize ? "border-primary-500" : "border-white"}`}
                      >
                        <Search width={32} height={32} className='w-6 h-6 my-auto' />
                        <input
                          ref={inputRef}
                          placeholder="¿Qué delicia estás buscando?"
                          defaultValue={valueRef.current}
                          className={`!bg-transparent w-full  outline-none border-none !ring-transparent placeholder:text-body ${initialize ? "no-autofill-bkg-primary placeholder:text-text-tertiary text-text-tertiary" : "placeholder:text-white no-autofill-bkg-white"}`}
                          name='search'
                          onChange={(e) => {
                            valueRef.current = e.target.value;
                            if (e.target.value.length >= 3) {
                              isSubmitting ? null : debounced(e);
                            }
                          }}
                        />
                        {
                          valueRef.current != "" ?
                            <button onClick={
                              () => {
                                clearResults()
                              }}>
                              <Close width={32} height={32} className='w-6 h-6 my-auto' />
                            </button>
                            : null
                        }
                      </div>
                      {
                        initialize && !isSubmitting ?
                          <>
                            {zeroResults ?
                              <p className="text-text-tertiary pt-4 text-body--l">¡Vaya! no hay resultados. Pero hay muchas otras joyas gastronómicas esperándote</p>
                              : null
                            }
                            <div className='grid grid-cols-1 gap-4 sm:gap-2 sm:grid-cols-3 pt-4'>
                              <div>
                                <Link className='text-subtitle text-subtitle--l flex items-end group hover:translate-x-1 hover:transition-transform' href='/es/productos-ibericos/jamon'>
                                  Productos <ChevronRight width={26} height={26} className='opacity-0 group-hover:opacity-100 group-hover:transition-opacity' />
                                </Link>
                                <SearchItems items={products} />
                              </div>
                              <div>
                                <Link className='text-subtitle text-subtitle--l flex items-end group hover:translate-x-1 hover:transition-transform' href='/es/escuela-del-jamon/recetas'>
                                  Recetas <ChevronRight width={26} height={26} className='opacity-0 group-hover:opacity-100 group-hover:transition-opacity' />
                                </Link>
                                <SearchItems items={recipes} />
                              </div>
                              <div>
                                <Link className='text-subtitle text-subtitle--l flex items-end group hover:translate-x-1 hover:transition-transform' href='/es/escuela-del-jamon'>
                                  Artículos <ChevronRight width={26} height={26} className='opacity-0 group-hover:opacity-100 group-hover:transition-opacity' />
                                </Link>
                                <SearchItems items={articles} />
                              </div>
                            </div>
                          </>
                          : null
                      }
                      {
                        isSubmitting ?
                          <div className='w-full h-full flex justify-center items-center flex-1'>
                            <MoonLoader color={initialize ? "rgb(199 137 56)" : "white"} />
                          </div>
                          : null
                      }
                    </div>
                  </Container>
                </>
                : <>{setMenuSearchOpen(false)}</>
            }
          </>
        )}
      </PopoverPanel>
    </Popover >
  );
}
